<template>
  <div class="file-upload-container">
    
    <!-- Zona de Drag & Drop -->
    <div 
      class="dropzone"
      :class="{ 
        'dropzone--dragging': isDragging,
        'dropzone--has-files': files.length > 0 
      }"
      @drop.prevent="handleDrop"
      @dragover.prevent="handleDragOver"
      @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave"
      @click="openFileSelector"
    >
      <!-- Icono y Texto -->
      <div class="dropzone__content">
        <div class="dropzone__icon-wrapper">
          <v-icon 
            :style="isDragging ? 'font-size: 40px; color: #005691;' : 'font-size: 36px; color: #9E9E9E;'"
            class="dropzone__icon"
          >
            {{ isDragging ? 'fa:fa-solid fa-download' : 'fa:fa-solid fa-cloud-arrow-up' }}
          </v-icon>
        </div>
        
        <div class="dropzone__text">
          <p class="dropzone__title" v-if="!isDragging">
            <span class="dropzone__action">Arrastra archivos aquí</span> o haz clic para seleccionar
          </p>
          <p class="dropzone__title dropzone__title--dragging" v-else>
            Suelta los archivos aquí
          </p>
          <p class="dropzone__subtitle" v-if="!isDragging && maxSizeMB">
            Tamaño máximo por archivo: {{ maxSizeMB }}MB
          </p>
        </div>
      </div>

      <!-- Input File Oculto -->
      <input 
        ref="fileInput" 
        type="file" 
        :multiple="multiple"
        :accept="accept"
        @change="handleFileSelect"
        class="dropzone__input"
      />
    </div>

    <!-- Lista de Archivos Seleccionados -->
    <transition-group 
      v-if="files.length > 0" 
      name="file-list"
      tag="div" 
      class="files-list mt-4"
    >
      <div 
        v-for="(file, index) in files" 
        :key="file.name + index"
        class="file-item"
      >
        <!-- Icono del Archivo -->
        <div class="file-item__icon-wrapper">
          <div class="file-item__icon" :style="{ backgroundColor: getFileColor(file.name) }">
            <i :class="getFileIcon(file.name)" style="font-size: 16px; color: white;"></i>
          </div>
        </div>

        <!-- Info del Archivo -->
        <div class="file-item__info">
          <div class="file-item__name">{{ file.name }}</div>
          <div class="file-item__size">{{ formatFileSize(file.size) }}</div>
        </div>

        <!-- Botón Eliminar -->
        <v-btn
          icon
          size="x-small"
          variant="text"
          color="grey-darken-1"
          class="file-item__remove"
          @click.stop="removeFile(index)"
        >
          <v-icon style="font-size: 14px;">fa:fa-solid fa-xmark</v-icon>
        </v-btn>
      </div>
    </transition-group>

    <!-- Mensaje de Error -->
    <transition name="fade">
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-3"
        closable
        @click:close="errorMessage = ''"
      >
        {{ errorMessage }}
      </v-alert>
    </transition>

  </div>
</template>

<script>
export default {
  name: 'FileUploadDragDrop',
  
  props: {
    // Array de archivos (v-model)
    modelValue: {
      type: Array,
      default: () => []
    },
    // Permitir múltiples archivos
    multiple: {
      type: Boolean,
      default: true
    },
    // Tipos de archivo aceptados (ej: "image/*,.pdf,.doc,.docx")
    accept: {
      type: String,
      default: ''
    },
    // Tamaño máximo por archivo en MB
    maxSizeMB: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      isDragging: false,
      dragCounter: 0,
      errorMessage: ''
    }
  },

  computed: {
    files() {
      return this.modelValue || []
    }
  },

  methods: {
    openFileSelector() {
      this.$refs.fileInput.click()
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      this.addFiles(files)
      // Limpiar el input para permitir seleccionar el mismo archivo de nuevo
      event.target.value = ''
    },

    handleDragEnter(e) {
      this.dragCounter++
      this.isDragging = true
    },

    handleDragLeave(e) {
      this.dragCounter--
      if (this.dragCounter === 0) {
        this.isDragging = false
      }
    },

    handleDragOver(e) {
      e.dataTransfer.dropEffect = 'copy'
    },

    handleDrop(e) {
      this.isDragging = false
      this.dragCounter = 0
      
      const files = Array.from(e.dataTransfer.files)
      this.addFiles(files)
    },

    addFiles(newFiles) {
      this.errorMessage = ''
      
      // Validar tamaño de archivos
      if (this.maxSizeMB) {
        const maxSizeBytes = this.maxSizeMB * 1024 * 1024
        const oversizedFiles = newFiles.filter(file => file.size > maxSizeBytes)
        
        if (oversizedFiles.length > 0) {
          this.errorMessage = `Algunos archivos superan el tamaño máximo de ${this.maxSizeMB}MB`
          // Filtrar archivos que no excedan el tamaño
          newFiles = newFiles.filter(file => file.size <= maxSizeBytes)
        }
      }

      // Si no es múltiple, reemplazar archivos existentes
      if (!this.multiple && newFiles.length > 0) {
        this.$emit('update:modelValue', [newFiles[0]])
      } else {
        // Agregar a los existentes
        const updatedFiles = [...this.files, ...newFiles]
        this.$emit('update:modelValue', updatedFiles)
      }
    },

    removeFile(index) {
      const updatedFiles = this.files.filter((_, i) => i !== index)
      this.$emit('update:modelValue', updatedFiles)
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },

    getFileIcon(filename) {
      const extension = filename.split('.').pop().toLowerCase()
      
      // Mapeo de extensiones a iconos de FontAwesome
      const iconMap = {
        // Imágenes
        jpg: 'fa-solid fa-file-image',
        jpeg: 'fa-solid fa-file-image',
        png: 'fa-solid fa-file-image',
        gif: 'fa-solid fa-file-image',
        svg: 'fa-solid fa-file-image',
        webp: 'fa-solid fa-file-image',
        
        // Documentos
        pdf: 'fa-solid fa-file-pdf',
        doc: 'fa-solid fa-file-word',
        docx: 'fa-solid fa-file-word',
        xls: 'fa-solid fa-file-excel',
        xlsx: 'fa-solid fa-file-excel',
        ppt: 'fa-solid fa-file-powerpoint',
        pptx: 'fa-solid fa-file-powerpoint',
        txt: 'fa-solid fa-file-lines',
        
        // Comprimidos
        zip: 'fa-solid fa-file-zipper',
        rar: 'fa-solid fa-file-zipper',
        '7z': 'fa-solid fa-file-zipper',
        
        // Videos
        mp4: 'fa-solid fa-file-video',
        avi: 'fa-solid fa-file-video',
        mov: 'fa-solid fa-file-video',
        
        // Audio
        mp3: 'fa-solid fa-file-audio',
        wav: 'fa-solid fa-file-audio',
        
        // Código
        js: 'fa-solid fa-file-code',
        html: 'fa-solid fa-file-code',
        css: 'fa-solid fa-file-code',
        json: 'fa-solid fa-file-code',
      }

      return iconMap[extension] || 'fa-solid fa-file'
    },

    getFileColor(filename) {
      const extension = filename.split('.').pop().toLowerCase()
      
      // Mapeo de extensiones a colores
      const colorMap = {
        // Imágenes - Azul
        jpg: '#2196F3',
        jpeg: '#2196F3',
        png: '#2196F3',
        gif: '#2196F3',
        svg: '#2196F3',
        webp: '#2196F3',
        
        // PDF - Rojo
        pdf: '#F44336',
        
        // Word - Azul oscuro
        doc: '#2B579A',
        docx: '#2B579A',
        
        // Excel - Verde
        xls: '#217346',
        xlsx: '#217346',
        
        // PowerPoint - Naranja
        ppt: '#D24726',
        pptx: '#D24726',
        
        // Comprimidos - Amarillo
        zip: '#FFC107',
        rar: '#FFC107',
        '7z': '#FFC107',
        
        // Videos - Púrpura
        mp4: '#9C27B0',
        avi: '#9C27B0',
        mov: '#9C27B0',
        
        // Audio - Rosa
        mp3: '#E91E63',
        wav: '#E91E63',
        
        // Texto - Gris
        txt: '#607D8B',
        
        // Código - Verde oscuro
        js: '#4CAF50',
        html: '#4CAF50',
        css: '#4CAF50',
        json: '#4CAF50',
      }

      return colorMap[extension] || '#757575'
    }
  }
}
</script>

<style scoped>
.file-upload-container {
  width: 100%;
}

/* Zona de Drop */
.dropzone {
  border: 2px dashed #BDBDBD;
  border-radius: 16px;
  background: #FAFAFA;
  padding: 32px 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.dropzone:hover {
  border-color: #005691;
  background: #F5F9FC;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 86, 145, 0.12);
}

.dropzone--dragging {
  border-color: #005691;
  background: linear-gradient(135deg, rgba(0, 86, 145, 0.08) 0%, rgba(0, 43, 73, 0.06) 100%);
  border-width: 3px;
  transform: scale(1.02);
  box-shadow: 0 12px 32px rgba(0, 86, 145, 0.2);
}

.dropzone--has-files {
  padding: 24px 20px;
}

.dropzone__input {
  display: none;
}

.dropzone__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 12px;
}

.dropzone__icon-wrapper {
  width: 72px;
  height: 72px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.dropzone:hover .dropzone__icon-wrapper,
.dropzone--dragging .dropzone__icon-wrapper {
  transform: scale(1.1);
  box-shadow: 0 8px 20px rgba(0, 86, 145, 0.15);
}

.dropzone__icon {
  transition: all 0.3s ease;
}

.dropzone__text {
  max-width: 400px;
}

.dropzone__title {
  font-size: 15px;
  font-weight: 600;
  color: #424242;
  margin: 0;
  line-height: 1.5;
}

.dropzone__title--dragging {
  color: #005691;
  font-size: 16px;
}

.dropzone__action {
  color: #005691;
  font-weight: 700;
}

.dropzone__subtitle {
  font-size: 12px;
  color: #757575;
  margin: 4px 0 0 0;
}

/* Lista de Archivos */
.files-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  transition: all 0.2s ease;
  gap: 12px;
}

.file-item:hover {
  border-color: #005691;
  box-shadow: 0 2px 8px rgba(0, 86, 145, 0.1);
  transform: translateX(4px);
}

.file-item__icon-wrapper {
  flex-shrink: 0;
}

.file-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.file-item:hover .file-item__icon {
  transform: scale(1.1);
}

.file-item__info {
  flex: 1;
  min-width: 0;
}

.file-item__name {
  font-size: 13px;
  font-weight: 600;
  color: #212121;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-item__size {
  font-size: 11px;
  color: #757575;
  margin-top: 2px;
}

.file-item__remove {
  flex-shrink: 0;
  opacity: 0.7;
}

.file-item__remove:hover {
  opacity: 1;
}

/* Animaciones */
.file-list-enter-active {
  animation: slideIn 0.3s ease-out;
}

.file-list-leave-active {
  animation: slideOut 0.2s ease-in;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(20px);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .dropzone {
    padding: 24px 16px;
  }

  .dropzone__icon-wrapper {
    width: 60px;
    height: 60px;
  }

  .dropzone__icon {
    font-size: 28px !important;
  }

  .dropzone__title {
    font-size: 14px;
  }

  .file-item {
    padding: 10px 12px;
  }
}
</style>
